import FormControlLabel, { FormControlLabelProps } from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import MuiRadioGroup, { RadioGroupProps as MuiRadioGroupProps } from '@mui/material/RadioGroup';
import { ChangeEvent, useCallback } from 'react';
import { Radio, IRadioProps } from '../Radio';

type IOption<T extends number | string> = {
    id: T;
    label: string;
};
export type IRadioGroupProps = Omit<MuiRadioGroupProps, 'onChange'> & {
    enableDOMAttributeRequired?: boolean;
    error?: boolean;
    helperText?: string;
    label?: string;
    disabled?: boolean;
    itemProps?: IRadioProps;
    labelPlacement?: FormControlLabelProps['labelPlacement'];
    name: string;
    options: IOption<string>[];
    readOnly?: boolean;
    required?: boolean;
    onChange?: (value: string) => void;
};

const RequiredRadio = ({ required: _, ...rest }: IRadioProps) => <Radio {...rest} required />;

export const RadioGroup = ({
    enableDOMAttributeRequired = false,
    error = false,
    helperText,
    label,
    name,
    options = [],
    disabled = false,
    readOnly,
    required = false,
    labelPlacement = 'end',
    onChange,
    ...rest
}: IRadioGroupProps) => {
    const handleChange = useCallback(
        (_: ChangeEvent<HTMLInputElement>, value: string) => {
            if (onChange && !readOnly && !disabled) {
                onChange(value);
            }
        },
        [disabled, readOnly, onChange]
    );

    return (
        <>
            {label && <FormLabel required={required}>{label}</FormLabel>}
            <MuiRadioGroup {...rest} name={name} onChange={handleChange} data-testid={`radio_group_${name}`}>
                {options.map((option) => (
                    <FormControlLabel
                        disabled={disabled}
                        labelPlacement={labelPlacement}
                        key={option.id}
                        control={
                            required && enableDOMAttributeRequired ? (
                                <RequiredRadio name={name} readOnly={readOnly} />
                            ) : (
                                <Radio name={name} readOnly={readOnly} />
                            )
                        }
                        required={false}
                        label={option.label}
                        value={option.id}
                    />
                ))}
            </MuiRadioGroup>
            {helperText && <FormHelperText error={error}>{helperText}</FormHelperText>}
        </>
    );
};
